
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchReviews, createReview } from "@/api/reviews";
import { Form, FormField, FormItem, FormControl } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, StarOff } from "lucide-react";
import { useForm } from "react-hook-form";
import { CreateReviewData } from "@/types/review";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import { formatDateForDisplay } from "@/utils/dateFormatters";

export default function Reviews() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const form = useForm<Omit<CreateReviewData, "Created at">>();
  
  const { data: reviews, isLoading } = useQuery({
    queryKey: ["reviews"],
    queryFn: fetchReviews,
  });

  const createReviewMutation = useMutation({
    mutationFn: createReview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      form.reset();
      toast({
        title: "Review submitted",
        description: "Thank you for your feedback!",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to submit review. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: Omit<CreateReviewData, "Created at">) => {
    createReviewMutation.mutate(data);
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const totalStars = 5;

    for (let i = 1; i <= totalStars; i++) {
      if (i <= rating) {
        stars.push(<Star key={i} className="w-5 h-5 text-yellow-400" />);
      } else {
        stars.push(<StarOff key={i} className="w-5 h-5 text-gray-300" />);
      }
    }

    return stars;
  };

  return (
    <div className="mt-12 p-6 bg-gray-50 rounded-lg max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Customer Reviews</h2>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mb-8">
          <FormField
            control={form.control}
            name="Author"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input placeholder="Your name" {...field} required />
                </FormControl>
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="Rating"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    {...field}
                    required
                  >
                    <option value="">Select rating</option>
                    {[5, 4, 3, 2, 1].map((num) => (
                      <option key={num} value={num}>
                        {num} Stars
                      </option>
                    ))}
                  </select>
                </FormControl>
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="Comment"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea 
                    placeholder="Your review..." 
                    className="h-24" 
                    {...field}
                    required 
                  />
                </FormControl>
              </FormItem>
            )}
          />
          
          <Button 
            type="submit" 
            disabled={createReviewMutation.isPending}
            className="w-full"
            variant="green"
          >
            {createReviewMutation.isPending ? "Submitting..." : "Submit Review"}
          </Button>
        </form>
      </Form>

      <div className="space-y-4 max-h-[32rem] overflow-y-auto pr-4">
        {isLoading ? (
          <div className="text-center py-4">Loading reviews...</div>
        ) : !reviews?.length ? (
          <div className="text-center py-4 text-gray-500">No reviews yet. Be the first to review!</div>
        ) : (
          reviews.map((review) => (
            <Card key={review.id}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <h3 className="font-semibold">{review.Author}</h3>
                    <div className="flex gap-0.5">
                      {renderStars(review.Rating)}
                    </div>
                    <p className="text-gray-600 mt-2">{review.Comment}</p>
                  </div>
                  <span className="text-sm text-gray-500">
                    {review["Created at"] ? format(new Date(review["Created at"]), 'MMM d, yyyy') : 'Invalid date'}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
